
import { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PlanCard } from './PlanCard';
import { Button } from "@/components/ui/button";
import { getFeaturedPlans } from '@/services/planService';
import { useToast } from "@/hooks/use-toast";
import { Plan } from '@/types';

interface FeaturedPlansProps {
  onViewAll: () => void;
}

export const FeaturedPlans = ({
  onViewAll
}: FeaturedPlansProps) => {
  const { toast } = useToast();
  
  const {
    data: featuredPlans = [] as Plan[],
    isLoading,
    error
  } = useQuery({
    queryKey: ['featuredPlans'],
    queryFn: getFeaturedPlans
  });
  
  // Show error toast when an error occurs
  useEffect(() => {
    if (error) {
      console.error('Error loading featured plans:', error);
      toast({
        title: "Fejl ved indlæsning af udvalgte abonnementer",
        description: "Der opstod en fejl ved indlæsning af udvalgte abonnementer.",
        variant: "destructive",
      });
    }
  }, [error, toast]);
  
  return (
    <section id="featured-plans" className="bg-gradient-to-b from-transparent to-secondary/30 py-[3px]">
      <div className="container mx-auto md:px-6 px-0">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Udvalgte Mobilabonnementer</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Vi har håndplukket disse abonnementer baseret på bl.a. pris, værdi og indhold.</p>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-[300px] bg-muted animate-pulse rounded-lg"></div>)}
          </div>
        ) : featuredPlans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredPlans.map((plan, index) => (
              <PlanCard 
                key={plan.id} 
                plan={plan} 
                className={`animate-fade-up animate-in-${index + 1}`} 
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-muted-foreground">
            Ingen udvalgte abonnementer tilgængelige i øjeblikket.
          </div>
        )}
        
        <div className="mt-12 text-center">
          <Button onClick={onViewAll} className="group">
            Se Alle Abonnementer
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};
